import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Compass, Leaf, Shield, MapPin } from "lucide-react";
import PublicLayout from "@/components/PublicLayout";
import Seo from "@/components/Seo";
import { api } from "@/lib/api";
import { usePage } from "@/context/PagesContext";
import { mediaUrl } from "@/lib/media";

const HEROES = [
  { src: "https://images.unsplash.com/photo-1528127269322-539801943592?crop=entropy&cs=srgb&fm=jpg&q=85&w=2400", caption: "Bai Tu Long Bay, Vietnam" },
  { src: "/api/uploads/philippines-f7cd82d086d34b-large.jpg", caption: "El Nido, Philippines" },
  { src: "https://images.unsplash.com/photo-1770848125591-2e97455bf21d?crop=entropy&cs=srgb&fm=jpg&q=85&w=2400", caption: "Angkor, Cambodia" },
  { src: "https://images.unsplash.com/photo-1656247203824-3d6f99461ba4?crop=entropy&cs=srgb&fm=jpg&q=85&w=2400", caption: "Tegallalang, Bali" },
  { src: "/api/uploads/laos-7f628fb09d3c4e-large.jpg", caption: "Kuang Si, Laos" },
  { src: "https://images.pexels.com/photos/31205510/pexels-photo-31205510.jpeg", caption: "Kyoto, Japan" },
];

const PHILOSOPHY = "https://images.unsplash.com/photo-1760006548203-14857f7a9266?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600";
const FOUNDER = "https://images.pexels.com/photos/31205510/pexels-photo-31205510.jpeg?auto=compress&cs=tinysrgb&w=1600";

export default function Home() {
  const { copy, seo } = usePage("home");
  const [destinations, setDestinations] = useState([]);
  const [journeys, setJourneys] = useState([]);
  const [testimonials, setTestimonials] = useState([]);
  const hero = useMemo(() => HEROES[Math.floor(Math.random() * HEROES.length)], []);

  useEffect(() => {
    api.get("/destinations").then((r) => setDestinations(r.data || []));
    api.get("/journeys").then((r) => setJourneys(r.data || []));
    api.get("/testimonials").then((r) => setTestimonials(r.data || []));
  }, []);

  const featuredTestimonial = testimonials[0];

  return (
    <PublicLayout>
      <Seo title={seo.title} description={seo.description} og_title={seo.og_title} og_description={seo.og_description} />
      {/* Hero */}
      <section data-testid="home-hero" className="relative h-screen min-h-[760px] w-full overflow-hidden">
        <img src={hero.src} alt={hero.caption} className="ken-img absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-jade/25" />
        <div className="absolute inset-0 bg-gradient-to-b from-jade/30 via-transparent to-jade/45" />
        <div className="relative z-10 h-full flex flex-col justify-center">
          <div className="max-w-5xl mx-auto px-6 md:px-12 w-full text-center">
            <h1 className="fade-up mt-8 font-serif text-ivory font-light tracking-tight text-4xl md:text-6xl lg:text-[80px] leading-[1.05]">
              {copy("hero_h1_line1", "Private journeys across Asia,")}<br/>
              <span className="italic">{copy("hero_h1_line2_italic", "designed around you.")}</span>
            </h1>
            <p className="fade-up delay-2 mt-8 text-ivory/85 mx-auto max-w-2xl text-base md:text-lg font-light leading-relaxed">
              {copy("hero_subcopy", "Tailor-made travel with trusted local guides, thoughtful planning, and support from start to finish.")}
            </p>
            <div className="fade-up delay-3 mt-12 flex flex-wrap gap-4 justify-center">
              <Link to="/contact" data-testid="hero-cta-start" className="btn-primary">{copy("hero_cta_primary", "Start Your Custom Trip")}</Link>
              <Link to="/destinations" data-testid="hero-cta-explore" className="btn-secondary !text-ivory !border-ivory hover:!bg-ivory/10">
                {copy("hero_cta_secondary", "Explore Destinations")}
              </Link>
            </div>
          </div>
        </div>
        {/* Location caption bottom-left */}
        <div data-testid="hero-caption" className="absolute bottom-8 left-8 md:left-12 z-10 fade-up delay-4 flex items-center gap-2 text-ivory/85 text-[11px] tracking-[0.18em] uppercase">
          <MapPin className="w-3.5 h-3.5" strokeWidth={1.2} />
          <span>{hero.caption}</span>
        </div>
      </section>

      {/* Pillars */}
      <section data-testid="home-pillars" className="bg-ivory py-28 md:py-40">
        <div className="max-w-[1500px] mx-auto px-6 md:px-12">
          <div className="grid md:grid-cols-12 gap-10 md:gap-20">
            <div className="md:col-span-4">
              <p className="eyebrow">{copy("pillars_eyebrow", "Our Promise")}</p>
              <h2 className="mt-6 font-serif text-4xl md:text-5xl text-jade font-light leading-tight">
                {copy("pillars_h2_part1", "Private journeys that bring you closer to the places, people, and ")}
                <span className="italic">{copy("pillars_h2_part2_italic", "experiences that matter to you.")}</span>
              </h2>
              <p className="mt-8 text-ink/70 text-[15px] font-light leading-relaxed max-w-md">
                {copy("pillars_intro", "We plan private journeys that go beyond the usual circuit. Opening doors across Asia through immersive experiences, exceptional guides, and quietly personal service.")}
              </p>
            </div>
            <div className="md:col-span-8 grid md:grid-cols-3 gap-12 md:gap-10">
              {[
                { num: "01", icon: Shield, title: copy("pillar_1_title", "Private by design"), body: copy("pillar_1_body", "We shape every journey around you. It feels personal, unhurried, and yours alone.") },
                { num: "02", icon: Compass, title: copy("pillar_2_title", "Expertly planned"), body: copy("pillar_2_body", "We travel these regions ourselves, so every detail is grounded in real, local knowledge.") },
                { num: "03", icon: Leaf, title: copy("pillar_3_title", "Seamlessly delivered"), body: copy("pillar_3_body", "We handle the logistics quietly in the background, so you can simply be there.") },
              ].map(({ num, icon: Icon, title, body }, i) => (
                <div key={num} className={`fade-up delay-${i+1} border-t border-mist/60 pt-8`} data-testid={`pillar-${i+1}`}>
                  <div className="flex items-center justify-between">
                    <span className="font-serif text-3xl text-copper italic">{num}</span>
                    <Icon strokeWidth={1} className="w-6 h-6 text-jade/70" />
                  </div>
                  <h3 className="mt-8 font-serif text-2xl md:text-[26px] text-jade leading-tight">{title}</h3>
                  <p className="mt-4 text-ink/70 text-[14.5px] leading-[1.75] font-light">{body}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Featured Destinations */}
      <section data-testid="home-destinations" className="bg-sand py-28 md:py-40">
        <div className="max-w-[1500px] mx-auto px-6 md:px-12">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-16">
            <div>
              <p className="eyebrow">{copy("destinations_eyebrow", "Where We Travel")}</p>
              <h2 className="mt-5 font-serif text-4xl md:text-5xl text-jade font-light leading-tight max-w-xl">
                {copy("destinations_h2", "Private journeys across Asia.")}
              </h2>
            </div>
            <Link to="/destinations" data-testid="home-destinations-link" className="text-[12px] tracking-[0.18em] uppercase text-jade flex items-center gap-2 group">
              {copy("destinations_link_label", "All Destinations")} <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" strokeWidth={1} />
            </Link>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
            {destinations.slice(0, 6).map((d, i) => (
              <Link
                key={d.slug}
                to={`/destinations/${d.slug}`}
                data-testid={`home-destination-${d.slug}`}
                className={`group relative overflow-hidden aspect-[4/5] block fade-up delay-${(i % 4) + 1}`}
              >
                <img src={d.hero_image} alt={d.name} loading="lazy" className="absolute inset-0 w-full h-full object-cover transition-transform duration-[1200ms] group-hover:scale-[1.04]" />
                <div className="absolute inset-0 bg-gradient-to-b from-transparent via-transparent to-jade/85" />
                <div className="absolute inset-0 p-8 flex flex-col justify-end">
                  <p className="eyebrow text-ivory/80">{d.tagline}</p>
                  <h3 className="mt-2 font-serif text-3xl md:text-4xl text-ivory leading-tight">{d.name}</h3>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Philosophy */}
      <section data-testid="home-philosophy" className="bg-ivory py-28 md:py-44">
        <div className="max-w-[1500px] mx-auto px-6 md:px-12 grid md:grid-cols-12 gap-12 items-center">
          <div className="md:col-span-5 md:order-2">
            <img src={mediaUrl(copy("philosophy_image", PHILOSOPHY))} alt="A quiet artisan moment" loading="lazy" className="w-full aspect-[4/5] object-cover" />
          </div>
          <div className="md:col-span-7 md:order-1 md:pr-12">
            <p className="eyebrow">{copy("philosophy_eyebrow", "Our Approach")}</p>
            <h2 className="mt-5 font-serif text-4xl md:text-6xl text-jade font-light leading-[1.05]">
              {copy("philosophy_h2_part1", "We create private journeys")}<br/>
              <span className="italic">{copy("philosophy_h2_part2_italic", "shaped around how you want to travel.")}</span>
            </h2>
            <p className="mt-8 text-ink/75 text-[16px] md:text-[17px] font-light leading-[1.8] max-w-xl">
              {copy("philosophy_body_1", "An afternoon with a calligrapher in Hanoi's Old Quarter. A morning with a chef in Hoi An. A private cruise on the Mekong, with no other guests aboard. The Bayon at first light, almost alone. A long lazy lunch by a quiet bay in Palawan.")}
            </p>
            <p className="mt-5 text-ink/75 text-[16px] md:text-[17px] font-light leading-[1.8] max-w-xl">
              {copy("philosophy_body_2", "However you want to travel, we plan it around you.")}
            </p>
            <Link to="/about" data-testid="home-about-link" className="mt-10 inline-flex items-center gap-3 text-[12px] tracking-[0.18em] uppercase text-jade group">
              {copy("philosophy_link_label", "Read Our Approach")}
              <ArrowRight strokeWidth={1} className="w-4 h-4 transition-transform group-hover:translate-x-1" />
            </Link>
          </div>
        </div>
      </section>

      {/* Founder's Note */}
      <section data-testid="home-founder" className="bg-sand py-28 md:py-40">
        <div className="max-w-[1500px] mx-auto px-6 md:px-12 grid md:grid-cols-12 gap-12 md:gap-20 items-center">
          <div className="md:col-span-5">
            <img src={mediaUrl(copy("founder_image", FOUNDER))} alt="A quiet morning at the studio" loading="lazy" className="w-full aspect-[4/5] object-cover" />
          </div>
          <div className="md:col-span-7 md:pl-6">
            <p className="eyebrow">{copy("founder_eyebrow", "A note from the founders")}</p>
            <p className="mt-10 font-serif text-3xl md:text-5xl text-jade font-light italic leading-[1.18]">
              {copy("founder_quote", "\"Asia rewards thoughtful travel, and every traveller deserves a journey shaped with care, knowledge, and time.\"")}
            </p>
            <p className="mt-10 text-ink/75 text-[16px] md:text-[17px] font-light leading-[1.85] max-w-xl">
              {copy("founder_body", "Between us we have spent more than two decades planning travel across Asia. Every itinerary we build starts the same way. With a real conversation about how you want to travel, and an honest sense of what will make the trip yours.")}
            </p>
            <div className="mt-12 flex items-center gap-5">
              <span className="block w-12 h-px bg-jade/40" />
              <div>
                <p className="font-serif text-jade text-2xl italic">{copy("founder_signature", "Allister & John")}</p>
                <p className="eyebrow text-ink/55 mt-1">{copy("founder_signature_subtitle", "Founders, Abbot Asia")}</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Sample Journeys */}
      <section data-testid="home-journeys" className="bg-jade text-ivory py-28 md:py-40">
        <div className="max-w-[1500px] mx-auto px-6 md:px-12">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-16">
            <div>
              <p className="eyebrow text-copper">{copy("journeys_eyebrow", "Sample Journeys")}</p>
              <h2 className="mt-5 font-serif text-4xl md:text-5xl font-light leading-tight max-w-xl text-ivory">
                {copy("journeys_h2_line1", "A few of the stories")}<br/>{copy("journeys_h2_line2", "we are telling now.")}
              </h2>
            </div>
            <Link to="/journeys" data-testid="home-journeys-link" className="text-[12px] tracking-[0.18em] uppercase text-ivory flex items-center gap-2 group">
              {copy("journeys_link_label", "All Journeys")} <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" strokeWidth={1} />
            </Link>
          </div>
          <div className="space-y-8">
            {journeys.slice(0, 3).map((j, i) => (
              <Link
                key={j.slug}
                to={`/journeys/${j.slug}`}
                data-testid={`home-journey-${j.slug}`}
                className={`fade-up delay-${(i % 3) + 1} grid md:grid-cols-12 gap-6 md:gap-10 items-center group border-b border-ivory/15 pb-8`}
              >
                <div className="md:col-span-5 aspect-[4/3] overflow-hidden">
                  <img src={j.hero_image} alt={j.title} loading="lazy" className="w-full h-full object-cover transition-transform duration-[1200ms] group-hover:scale-[1.04]" />
                </div>
                <div className="md:col-span-7">
                  <p className="eyebrow text-copper">{j.duration_days} days · From £{j.from_price_gbp.toLocaleString()} pp</p>
                  <h3 className="mt-3 font-serif text-3xl md:text-4xl text-ivory leading-tight">{j.title}</h3>
                  <p className="mt-4 text-ivory/70 text-[15px] font-light leading-relaxed max-w-xl">{j.summary}</p>
                  <span className="mt-6 inline-flex items-center gap-2 text-[12px] tracking-[0.18em] uppercase text-ivory/90">
                    See the Itinerary
                    <ArrowRight strokeWidth={1} className="w-4 h-4 transition-transform group-hover:translate-x-1" />
                  </span>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonial */}
      {featuredTestimonial && (
        <section data-testid="home-testimonial" className="bg-ivory py-28 md:py-40">
          <div className="max-w-3xl mx-auto px-6 md:px-12 text-center">
            <p className="eyebrow">{copy("testimonial_eyebrow", "Notes From Our Travellers")}</p>
            <p className="mt-10 font-serif text-3xl md:text-4xl text-jade font-light italic leading-[1.3]" data-testid="testimonial-quote">
              {/^["“].*["”]$/.test(featuredTestimonial.quote) ? featuredTestimonial.quote : `"${featuredTestimonial.quote}"`}
            </p>
            <p className="mt-10 eyebrow text-ink/60" data-testid="testimonial-attribution">
              {[featuredTestimonial.attribution, featuredTestimonial.location].filter(Boolean).join(", ")}
            </p>
          </div>
        </section>
      )}

      {/* CTA */}
      <section data-testid="home-cta" className="bg-sand py-28 md:py-40">
        <div className="max-w-3xl mx-auto px-6 md:px-12 text-center">
          <p className="eyebrow">{copy("cta_eyebrow", "Begin")}</p>
          <h2 className="mt-8 font-serif text-4xl md:text-6xl text-jade font-light leading-[1.05]">
            {copy("cta_h2_part1", "Let's design something")}<br/>
            <span className="italic">{copy("cta_h2_part2_italic", "just for you.")}</span>
          </h2>
          <p className="mt-8 text-ink/75 text-[16px] md:text-[17px] font-light leading-[1.8] max-w-xl mx-auto">
            {copy("cta_body", "Tell us a little about how you like to travel, and we will put together a considered proposal. No obligation, no rush.")}
          </p>
          <div className="mt-12">
            <Link to="/contact" data-testid="cta-bottom" className="btn-primary">{copy("cta_label", "Start Your Custom Trip")}</Link>
          </div>
        </div>
      </section>
    </PublicLayout>
  );
}
